import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import Cookies from "js-cookie";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Lottie from "lottie-react";

import PrivacyPolicyModal from "./privacyPolicy/PrivacyPolicyModal";
import SaasModal from "./privacyPolicy/SaasModel";
import EulaModal from "./privacyPolicy/eulaModal";

import dentalLoginAnimation from "../components/animation/online doctor.json";
import styles from "./Login.module.scss";

export default function Login() {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSAASModal, setShowSAASModal] = useState(false);
  const [showEulaModal, setShowEulaModal] = useState(false);
  const [hasGuestRef, setHasGuestRef] = useState(false);
  const [privacyContent, setPrivacyContent] = useState("");
  const [saasContent, setSaasContent] = useState("");
  const [eulaContent, setEulaContent] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const guestRef = params.get("guest_purchase_reference");
    setHasGuestRef(Boolean(guestRef));
    if (guestRef) {
      localStorage.setItem("guest_purchase_reference", guestRef);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const guestRef = params.get("guest_purchase_reference");
    if (guestRef) {
      localStorage.setItem("guest_purchase_reference", guestRef);
    }
  }, []);

  const login = useMutation({
    mutationFn: () =>
      axios.get(`/auth/get_dentally_request_auth_link?capture=default`),
    onSuccess: (res) => window.location.replace(res.data),
  });

  const loadDoc = async (id, setter, modalSetter) => {
    try {
      const res = await axios.get(`document_agreement/${id}`);
      setter(res.data.document);
    } catch {
      setter("Failed to load document.");
    } finally {
      modalSetter(true);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.backgroundGlowOne} />
      <div className={styles.backgroundGlowTwo} />
      <div className={styles.gridOverlay} />

      <div className={styles.centerWrap}>
        <div className={styles.card}>
          {/* LEFT */}
          <div className={styles.left}>
            <div className={styles.leftTop}>
              <div className={styles.brandRow}>
                <img src="/logo.png" className={styles.logo} alt="Medicube" />
              </div>

                <div className={styles.orangeText}>
                  <span
                    className={styles.fillText}
                    data-text="Streamlined Consent Management"
                  >
                    Streamlined Consent Management
                  </span>
                </div>

              <div className={styles.tagline}>
              
                <div className={styles.captionText}>
                  Connect with Dentally to automatically sync patient and
                  treatment data for seamless consent workflows.
                </div>
              </div>
            </div>

            <div className={styles.visualPanel}>
              <div className={styles.visualHeader}>
                <span className={styles.visualPill}>Secure</span>
                <span className={styles.visualPill}>Fast</span>
                <span className={styles.visualPill}>Dental-ready</span>
              </div>

              <div className={styles.animationStage}>
                <div className={styles.lottieGlow} />
                <div className={styles.lottieCard}>
  <Lottie
    animationData={dentalLoginAnimation}
    loop
    autoplay
    className={styles.lottieAnimation}
  />
</div>
              </div>

              <div className={styles.featureGrid}>
                <div className={styles.featureCard}>
                  <div className={styles.featureTitle}>Auto-prefill</div>
                  <div className={styles.featureText}>
                    Patient and treatment details prepared for faster workflows.
                  </div>
                </div>

                <div className={styles.featureCard}>
                  <div className={styles.featureTitle}>Safer onboarding</div>
                  <div className={styles.featureText}>
                    Minimal access request with secure, standard authorization.
                  </div>
                </div>

                <div className={styles.featureCard}>
                  <div className={styles.featureTitle}>Paperless flow</div>
                  <div className={styles.featureText}>
                    Keep consent steps organized and easier to manage.
                  </div>
                </div>

                <div className={styles.featureCard}>
                  <div className={styles.featureTitle}>Quick setup</div>
                  <div className={styles.featureText}>
                    Connect once and continue with a smoother clinical process.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className={styles.right}>
            <button
              className={styles.aboutBtn}
              onClick={() => {
                window.location.href = "https://medicube.site/";
              }}
            >
              About
            </button>

            <div className={styles.loginPanel}>
              <div className={styles.panelGlow} />
              <div className={styles.content}>
               <div className={styles.welcomeTag}>
  <span
    className={styles.welcomeFillText}
    data-text="Welcome to Medicube"
  >
    Welcome to Medicube
  </span>
</div>
<h1 className={styles.loginHeading}>Login with Dentally</h1>

                <p className={styles.subText}>
                  Your Dentally data will be used to prefill patient and
                  treatment plan details.
                </p>

                {hasGuestRef && (
                  <div className={styles.alertWrap}>
                    <Alert
                      severity="warning"
                      variant="outlined"
                      sx={{
                        borderRadius: "16px",
                        borderWidth: 1.5,
                        px: 2,
                        py: 1.5,
                        alignItems: "flex-start",
                        background: "rgba(255,255,255,0.82)",
                        backdropFilter: "blur(12px)",
                        "& .MuiAlertTitle-root": {
                          fontWeight: 800,
                          mb: 0.5,
                        },
                        "& .MuiAlert-icon": {
                          mt: "2px",
                        },
                      }}
                    >
                      <AlertTitle>Important notice</AlertTitle>
                      When you connect to Dentally via MediCube for the first
                      time, Dentally may display a warning regarding third-party
                      access and data security. This is a standard message shown
                      when authorising an external application.
                      <br />
                      <br />
                      You have nothing to worry about. Medicube only requests
                      the minimum information required to make your workflow
                      seemless, and we take reasonable, industry-standard
                      measures to safeguard your information. You can find more
                      information in our{" "}
                      <span
                        style={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontWeight: 700,
                        }}
                        onClick={() =>
                          loadDoc(3, setPrivacyContent, setShowPrivacyModal)
                        }
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            loadDoc(3, setPrivacyContent, setShowPrivacyModal);
                          }
                        }}
                      >
                        Privacy Policy
                      </span>
                      .
                    </Alert>
                  </div>
                )}

               <div className={styles.loginBtnWrap}>
  <div className={styles.loginBtnRing} />
  <Button
    className={styles.loginBtn}
    onClick={() => login.mutate()}
  >
    <span className={styles.loginBtnText}>Continue with</span>
    <img src="/dentally-Photoroom.png" alt="Dentally" />
  </Button>
</div>

                <div className={styles.noteBox}>
                  <div className={styles.noteTitle}>Protected connection</div>
                  <div className={styles.noteText}>
                    We only request the information needed to support your
                    consent workflow.
                  </div>
                </div>

                <p className={styles.privacy}>
                  By continuing, you agree to our{" "}
                  <span
                    onClick={() =>
                      loadDoc(3, setPrivacyContent, setShowPrivacyModal)
                    }
                  >
                    Privacy Policy
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className={styles.footer}>
        <img src="/logo.png" alt="Medicube" />
        <p>© 2024 Medicube. All rights reserved.</p>
        <div className={styles.links}>
          <span onClick={() => loadDoc(3, setPrivacyContent, setShowPrivacyModal)}>
            Privacy Policy
          </span>
          <span className={styles.separator}>|</span>
          <span onClick={() => loadDoc(2, setSaasContent, setShowSAASModal)}>
            SAAS
          </span>
          <span className={styles.separator}>|</span>
          <span onClick={() => loadDoc(1, setEulaContent, setShowEulaModal)}>
            EULA
          </span>
        </div>
      </footer>

      <PrivacyPolicyModal
        open={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        content={privacyContent}
      />
      <SaasModal
        open={showSAASModal}
        onClose={() => setShowSAASModal(false)}
        content={saasContent}
      />
      <EulaModal
        open={showEulaModal}
        onClose={() => setShowEulaModal(false)}
        content={eulaContent}
      />
    </div>
  );
}